import { prototypes } from '../prototypes/registry';
import type { Prototype } from '../prototypes/types';
import {
  clearPacks,
  createInstallPrompt,
  downloadPack,
  isInstalled,
  readPackStatus,
  serviceWorkerSupported,
  storageEstimate,
  type PackStatus,
} from './pwa';

/**
 * The "Available Offline" screen from section 6.1.
 *
 * The plan asks for offline state to be *visible* rather than invisible,
 * because the thing being tested is not only whether caching works but whether
 * a teacher can tell what the device has kept. Hence Indonesian labels: this is
 * the one part of the shell aimed at the people who would actually use it, not
 * at us.
 *
 * It reports the awkward states honestly. A pack stored before a new build was
 * deployed says "perlu unduh ulang" rather than claiming to be ready, because
 * discovering that in a classroom with no signal is the failure we are trying
 * to design out.
 */

const STATUS_LABEL: Record<PackStatus, string> = {
  ready: 'Tersimpan',
  unavailable: 'Belum diunduh',
  stale: 'Perlu unduh ulang',
  downloading: 'Mengunduh…',
};

const downloadable = () => prototypes.filter((p): p is Prototype & { load: () => Promise<unknown> } => Boolean(p.load));

export function renderOfflinePanel(host: HTMLElement): () => void {
  const section = document.createElement('section');
  section.className = 'offline';

  const installPrompt = createInstallPrompt();
  const listeners = new AbortController();

  section.innerHTML = `
    <div class="offline__head">
      <h3>Tersedia offline</h3>
      <div class="offline__badges">
        <span class="offline__net" data-net></span>
        <span class="offline__mode" data-mode></span>
      </div>
    </div>
    <p class="offline__intro" data-intro></p>
    <div class="offline__packs" data-packs></div>
    <div class="offline__foot">
      <span data-storage></span>
      <div class="offline__actions">
        <button type="button" class="offline__install" data-install hidden>Pasang aplikasi</button>
        <button type="button" class="offline__clear" data-clear>Hapus unduhan</button>
      </div>
    </div>
  `;

  host.appendChild(section);

  const netOut = section.querySelector('[data-net]') as HTMLElement;
  const modeOut = section.querySelector('[data-mode]') as HTMLElement;
  const introOut = section.querySelector('[data-intro]') as HTMLElement;
  const packHost = section.querySelector('[data-packs]') as HTMLElement;
  const storageOut = section.querySelector('[data-storage]') as HTMLElement;
  const installButton = section.querySelector('[data-install]') as HTMLButtonElement;
  const clearButton = section.querySelector('[data-clear]') as HTMLButtonElement;

  const supported = serviceWorkerSupported();
  const production = import.meta.env.PROD;

  introOut.textContent = !supported
    ? 'Peramban ini tidak mendukung penyimpanan offline.'
    : !production
      ? 'Mode pengembangan: service worker tidak aktif. Jalankan `pnpm build && pnpm preview` untuk menguji offline.'
      : 'Unduh paket pelajaran agar tetap bisa dimainkan tanpa internet.';

  const syncNetwork = () => {
    const online = navigator.onLine;
    netOut.textContent = online ? 'Online' : 'Offline';
    netOut.dataset['state'] = online ? 'online' : 'offline';
  };

  const syncMode = () => {
    const installed = isInstalled();
    modeOut.textContent = installed ? 'Terpasang' : 'Tab peramban';
    modeOut.dataset['state'] = installed ? 'installed' : 'browser';
  };

  const syncInstall = () => {
    installButton.hidden = !installPrompt.available();
  };

  const rows = new Map<string, { status: HTMLElement; button: HTMLButtonElement }>();

  const setRow = (id: string, status: PackStatus) => {
    const row = rows.get(id);
    if (!row) return;
    row.status.textContent = STATUS_LABEL[status];
    row.status.dataset['state'] = status;
    row.button.disabled = status === 'downloading' || status === 'ready';
    row.button.textContent = status === 'stale' ? 'Unduh ulang' : 'Unduh';
  };

  const refreshStorage = async () => {
    const usage = await storageEstimate();
    storageOut.textContent = usage ? `Terpakai ${usage}` : '';
  };

  const refreshAll = async () => {
    await Promise.all(
      downloadable().map(async (prototype) => {
        const state = await readPackStatus(prototype);
        setRow(prototype.id, state.status);
      }),
    );
    await refreshStorage();
  };

  for (const prototype of downloadable()) {
    const row = document.createElement('div');
    row.className = 'offline__pack';

    const name = document.createElement('span');
    name.className = 'offline__pack-name';
    name.textContent = prototype.title;

    const status = document.createElement('span');
    status.className = 'offline__pack-status';
    status.textContent = STATUS_LABEL.unavailable;
    status.dataset['state'] = 'unavailable';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'offline__pack-button';
    button.textContent = 'Unduh';
    button.disabled = !supported || !production;

    button.addEventListener(
      'click',
      async () => {
        setRow(prototype.id, 'downloading');
        const state = await downloadPack(prototype);
        setRow(prototype.id, state.status);
        await refreshStorage();
      },
      { signal: listeners.signal },
    );

    row.append(name, status, button);
    packHost.appendChild(row);
    rows.set(prototype.id, { status, button });
  }

  installButton.addEventListener(
    'click',
    async () => {
      await installPrompt.show();
      syncInstall();
    },
    { signal: listeners.signal },
  );

  clearButton.addEventListener(
    'click',
    async () => {
      await clearPacks(prototypes);
      await refreshAll();
    },
    { signal: listeners.signal },
  );

  window.addEventListener('online', syncNetwork, { signal: listeners.signal });
  window.addEventListener('offline', syncNetwork, { signal: listeners.signal });
  installPrompt.onChange(syncInstall);

  syncNetwork();
  syncMode();
  syncInstall();
  void refreshAll();

  return () => listeners.abort();
}
